import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionProps } from "@insite/mobius/Accordion";
import AccordionSection from "@insite/mobius/AccordionSection";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SurAccordion from "@tSurya/Components/SurAccordion";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import uniqueId from "lodash/uniqueId";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titleLink = "titleLink",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: LinkModel[];
    };
}

export interface ResourcesStyle {
    specificationsAccordion?: AccordionProps;
    resourceTitle?: InjectableCss;
    resourceLink?: LinkPresentationProps;
}

export const resourcesStyle: ResourcesStyle = {
    specificationsAccordion: {
        headingLevel: 2,
        css: css`
            dt {
                background-color: ${getColor("common.backgroundContrast")};
                padding-bottom: 5px;
                button {
                    background-color: ${getColor("common.backgroundContrast")};
                    padding: 0px;
                    border: none;
                }
                > button[aria-expanded="true"] {
                    background-color: ${getColor("common.backgroundContrast")};
                    padding: 0px;
                    border: none;
                }
            }
            dd {
                border: none;
            }
        `,
    },
    resourceTitle: {
        css: css``,
    },
    resourceLink: {
        css: css``,
    },
};

const styles = resourcesStyle;

export const SurMobileResources: FC<OwnProps> = ({ fields }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);

    return (
        <StyledWrapper>
            <SurAccordion title={fields.title} links={links} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurMobileResources,
    definition: {
        group: "Basic",
        displayName: "Sur Resources",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
